/**
 * Z-Index Semantic Constants
 *
 * Centralized z-index values for TypeScript/TSX usage.
 * These map 1:1 to CSS custom properties in global.css.
 *
 * ⚠️ IMPORTANT: ALWAYS use these constants instead of hardcoded numbers!
 *
 * @see src/global.css - CSS variable definitions
 * @see CLAUDE.md - Z-Index Hierarchy System documentation
 *
 * @module constants/zIndex
 */

// ===== LAYER 1: BASE (0-99) =====
// Map and foundational elements

/** Base layer (map background, default z-index) */
export const Z_INDEX_BASE = 0;

/** MapLibre base layer */
export const Z_INDEX_MAP = 0;

/** Map controls (zoom buttons, reset, layer toggle) */
export const Z_INDEX_MAP_CONTROLS = 50;

/** Preview marker (above task markers, below controls) */
export const Z_INDEX_PREVIEW_MARKER = 95;

/** Always-on user live-location marker (below map controls, near preview marker) */
export const Z_INDEX_USER_LOCATION = 90;

// ===== LAYER 2: UI OVERLAYS & PANELS (100-999) =====
// Sidebars, overlays, form panels

/** TasksPanel sidebar */
export const Z_INDEX_SIDEBAR = 100;

/** FloatingSettingsBar, NavigationBar */
export const Z_INDEX_OVERLAY = 150;

/** TaskFormContainer desktop panel */
export const Z_INDEX_PANEL_DESKTOP = 250;

/** Mobile panel backdrop overlay */
export const Z_INDEX_PANEL_MOBILE_OVERLAY = 250;

/** TaskFormContainer mobile panel */
export const Z_INDEX_PANEL_MOBILE = 300;

// ===== LAYER 3: TOOLTIPS & INSTRUCTIONS (1000-1999) =====
// Tooltips, instructional overlays, guides

/** Basic tooltips (AppTooltip) */
export const Z_INDEX_TOOLTIP = 1000;

/** DrawingInstructions, MapToolbox, ToolbarGuidePanel */
export const Z_INDEX_INSTRUCTIONS = 1100;

/** GuideCarousel overlay */
export const Z_INDEX_GUIDE_CAROUSEL = 1200;

/** Main.tsx tooltip default */
export const Z_INDEX_TOOLTIP_DEFAULT = 1300;

/** Context menus (right-click menus, MapContextMenu) */
export const Z_INDEX_TOOLTIP_CONTEXT = 1500;

// ===== LAYER 4: DROPDOWNS & AUTOCOMPLETE (2000-2999) =====
// Autocomplete, select menus, dropdowns

/** Autocomplete dropdowns (AddressSearchBar, RoadSelector, AssigneeAutocomplete, LocationFilter) */
export const Z_INDEX_AUTOCOMPLETE = 2000;

/** Standard dropdowns (LanguageSelector, Select) */
export const Z_INDEX_DROPDOWN = 2100;

/** Toolbar guide panel (above search bars, below standard dropdowns) */
export const Z_INDEX_TOOLBAR_GUIDE = 2150;

/** Select menus */
export const Z_INDEX_SELECT = 2200;

// ===== LAYER 5: MODALS & DIALOGS (3000-3999) =====
// Modal dialogs, context menus inside modals

/** Modal backdrops */
export const Z_INDEX_MODAL_BACKDROP = 3000;

/** Modal content */
export const Z_INDEX_MODAL = 3001;

/** Context menus inside modals */
export const Z_INDEX_CONTEXT_MENU = 3100;

/** DatePicker inside modals (above modal content, below toasts) */
export const Z_INDEX_MODAL_DATEPICKER = 3200;

// ===== LAYER 6: TOASTS & NOTIFICATIONS (4000-4999) =====
// Toast notifications, system alerts

/** Toast notifications (UpdatePrompt, ReloadPrompt) */
export const Z_INDEX_TOAST = 4000;

/** System notifications - above mobile modals for visibility */
export const Z_INDEX_NOTIFICATION = 8200;

/** Bulk actions bar (multi-geometry selection actions) */
export const Z_INDEX_BULK_ACTIONS_BAR = 4500;

// ===== LAYER 7: SPECIAL OVERLAYS (5000-8000) =====
// Popovers, guided tours, mobile-specific layers

/** Popovers */
export const Z_INDEX_POPOVER = 5000;

/** Minimized mobile panel (collapsed form bar at bottom) */
export const Z_INDEX_PANEL_MOBILE_MINIMIZED = 5500;

/** Onboarding guided tours */
export const Z_INDEX_GUIDED_TOUR = 5500;

/** Mobile TasksPanel (highest mobile panel) */
export const Z_INDEX_MOBILE_PANEL_MAX = 6000;

/** Mobile forms (highest layer, handles transform contexts) */
export const Z_INDEX_MOBILE_MODAL = 8000;

/** DatePicker in mobile forms (above mobile modal) */
export const Z_INDEX_MOBILE_MODAL_DATEPICKER = 8100;

/** Dropdowns/comboboxes rendered inside a SlidePanel form (above SlidePanel z 8000) */
export const Z_INDEX_PANEL_DROPDOWN = 8100;

/** Critical confirmation modals (UnsavedChangesDialog, ConfirmNavigationDialog, DeleteConfirmationDialog) */
export const Z_INDEX_MODAL_CRITICAL = 9000;

/** Critical confirmation modals backdrop (just below content) */
export const Z_INDEX_MODAL_CRITICAL_BACKDROP = 8999;

// ===== COMPONENT-SPECIFIC LAYERS =====
// Special layers for specific UI components

/** Line segment midpoint handles (geometry editing) */
export const Z_INDEX_SEGMENT_INDICATOR = 999;

/** Line point drag handles (geometry editing) */
export const Z_INDEX_POINT_HANDLE = 1001;

/** CustomColorPanel (color picker overlay, above instructions tooltip) */
export const Z_INDEX_COLOR_PANEL = 1150;

// ===== INTERNAL STACKING CONTEXTS =====
// Relative z-index values within isolated containers
// Use these for layering elements WITHIN a component (not across components)

/** Base layer within container */
export const Z_INDEX_INTERNAL_BASE = 0;

/** First layer above base */
export const Z_INDEX_INTERNAL_LAYER_1 = 1;

/** Second layer */
export const Z_INDEX_INTERNAL_LAYER_2 = 2;

/** Third layer (max for simple stacks) */
export const Z_INDEX_INTERNAL_LAYER_3 = 3;

/** Internal controls (close buttons, icons, badges) */
export const Z_INDEX_INTERNAL_CONTROLS = 10;

/** Internal overlays (resize handles, drag overlays) */
export const Z_INDEX_INTERNAL_OVERLAY = 20;

/** Internal modals (nested dialogs within containers) */
export const Z_INDEX_INTERNAL_MODAL = 30;
